import { Trans, useTranslation } from "react-i18next";
import AccountHeader from "../../AccountHeader";
import { Alert, Container, Stack, Typography, type ChipProps } from "@mui/material";
import { type ReactElement, useMemo } from "react";
import tKey from "../../../Common/Utils/tKey";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LocalShippingOutlinedIcon from "@mui/icons-material/LocalShippingOutlined";
import RadioButtonUncheckedRoundedIcon from "@mui/icons-material/RadioButtonUncheckedRounded";
import { useSearchParams } from "react-router";
import useStoreFlagError from "../../../Common/_hooks/Store/useStoreFlagError";
import StoreSetupCard from "./StoreSetupCard";
import StoreShippingTablesEmbed from "./StoreShippingTablesEmbed";
import StoreConnectionStepper from "./StoreConnectionStepper";
import useStoreData from "../../../Common/_hooks/Store/useStoreData";

export default function ShopifyConnect() {
  const [t] = useTranslation("account-v2");
  const [tR] = useTranslation("routes");

  const [searchParams] = useSearchParams();

  const storeSlug = "shopify";
  const setupUrl = tR("/account/stores/connect/{{store}}", { store: storeSlug });

  const store = useStoreData(storeSlug);

  const isFlagged = useMemo(() => store?.get("flagged"), [store]);
  const isMissingScope = !!store?.get("is_missing_scope");
  const flagErrorMessage = useStoreFlagError(storeSlug);
  const isConnected = !!store && !isFlagged && !isMissingScope;
  const connectUrl = `${setupUrl}?connect=1`;
  const startConnectUrl = "https://apps.shopify.com/art-of-where";

  const connectErrorMessage = useMemo(() => {
    const error = searchParams.get("error");
    if (!error) return null;

    switch (error) {
      case "access_denied":
        return t("It looks like the Shopify installation flow was interrupted. Follow the steps below again and confirm the installation to finish connecting your store.");
      case "already-exists":
        return t("This Shopify store is already linked to another Art of Where account. Please remove it from the other account first.");
      default:
        return t("An error occurred while connecting to your store. Please try again.");
    }
  }, [searchParams, t]);

  const connectionStatus = useMemo((): { label: string, color: ChipProps["color"], icon: ReactElement } => {
    if (isConnected) {
      return {
        label: t("Connected"),
        color: "success",
        icon: <CheckCircleIcon color="success" />,
      };
    }

    if (isFlagged) {
      return {
        label: t("Needs attention"),
        color: "error",
        icon: <ErrorOutlineRoundedIcon color="error" />,
      };
    }

    if (isMissingScope) {
      return {
        label: t("Needs attention"),
        color: "warning",
        icon: <ErrorOutlineRoundedIcon color="warning" />,
      };
    }

    return {
      label: t("Not connected"),
      color: "default",
      icon: <RadioButtonUncheckedRoundedIcon color="action" />,
    };
  }, [isConnected, isFlagged, isMissingScope, t]);

  return <div>
    <AccountHeader
      title={t("Set up your {{store}} store", { store: "Shopify" })}
    />

    <Container
      maxWidth="md"
      disableGutters
      style={{ margin: 0 }}
    >
      <Stack spacing={4}>
        <Typography variant="body1">
          {t("Connect your Shopify store first, then follow the shipping steps below to finish setting it up.")}
        </Typography>

        <StoreSetupCard
          icon={connectionStatus.icon}
          title={t("Connection")}
          status={connectionStatus.label}
          statusColor={connectionStatus.color}
        >
          <StoreConnectionStepper
            t={t}
            hasStoreStepLabel={t("Do you already have a Shopify store?")}
            noStoreTitle={t("Create your Shopify store")}
            noStoreDescription={<Typography variant="body1">
              <Trans
                t={t}
                i18nKey={tKey("Go to <a>Shopify.com</a> and create your store now.")}
                components={{
                  a: <a href="https://shopify.com/" target="_blank" />
                }}
              />
            </Typography>}
            connectStepLabel={t("Install the Art of Where app on Shopify")}
            connectSteps={[
              t("Click the button below to open the Art of Where app page in Shopify."),
              t("Sign in to Shopify if you are prompted to do so, then choose the correct store."),
              t("Click Install or Open and confirm the permissions Shopify requests."),
            ]}
            connectButtonLabel={t("Install app")}
            connectUrl={connectUrl}
            startConnectRequested={searchParams.get("connect") === "1"}
            startConnectUrl={startConnectUrl}
            connectErrorMessage={connectErrorMessage}
            isConnected={isConnected}
            isFlagged={!!isFlagged}
            isMissingScope={isMissingScope}
            flagErrorMessage={flagErrorMessage}
            connectedContent={<Typography variant="body1">
              {t("Your Shopify store is connected and ready for product publishing and order syncing.")}
            </Typography>}
          />
        </StoreSetupCard>

        <StoreSetupCard
          icon={<LocalShippingOutlinedIcon color="secondary" />}
          title={t("Shipping")}
          status={isConnected ? t("Manual step") : t("After connection")}
          statusColor={isConnected ? "warning" : "default"}
        >
          <Stack spacing={2} alignItems="flex-start">
            <Typography variant="body1">
              {t("Use weight-based rates in Shopify's General profile. They match our tables best.")}
            </Typography>

            <Alert severity="info">
              {t("For more precision, split Canada by province and the US by state or zone. This takes longer to configure.")}
            </Alert>

            <Stack spacing={1} sx={{ width: "100%" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t("Profile and zones")}
              </Typography>
              <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("<strong>Open Settings > Shipping and delivery</strong>, then open the <strong>General profile</strong>.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("In <strong>Fulfillment locations</strong>, make sure <strong>Art of Where</strong> is included in the profile.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("Under <strong>Shipping zones</strong>, add one zone for Canada and one for the US. Optional: split Canada by province and the US by state or zone.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("Add an <strong>International Tracked</strong> zone for non-DHL-only countries. For DHL-only countries, add one zone per country.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
              </ol>
            </Stack>

            <Stack spacing={1} sx={{ width: "100%" }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t("Rates and timing")}
              </Typography>
              <ol style={{ margin: 0, paddingLeft: "1.25rem" }}>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("In each zone, click <strong>Add shipping option</strong>. Set <strong>Rate type</strong> to <strong>Weight</strong>. Use our minimum and maximum ranges with the matching prices below.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("The Canada and US rates below are the <strong>Standard</strong> rates.")}
                    components={{ strong: <strong /> }}
                  />
                </li>
                <li>
                  <Trans
                    t={t}
                    i18nKey={tKey("For <strong>Transit time</strong>, leave it blank or select <strong>Custom</strong>. If you show it, use these average totals:")}
                    components={{ strong: <strong /> }}
                  />
                  <ul style={{ margin: "0.5rem 0 0", paddingLeft: "1.25rem" }}>
                    <li>
                      <Trans
                        t={t}
                        i18nKey={tKey("<strong>Canada:</strong> 6 to 12 business days total. About 5 production + 1 to 7 transit.")}
                        components={{ strong: <strong /> }}
                      />
                    </li>
                    <li>
                      <Trans
                        t={t}
                        i18nKey={tKey("<strong>US:</strong> 8 to 11 business days total. About 5 production + 3 to 6 transit.")}
                        components={{ strong: <strong /> }}
                      />
                    </li>
                    <li>
                      <Trans
                        t={t}
                        i18nKey={tKey("<strong>International Tracked:</strong> 15 to 35 business days total. About 5 production + 10 to 30 transit.")}
                        components={{ strong: <strong /> }}
                      />
                    </li>
                    <li>
                      <Trans
                        t={t}
                        i18nKey={tKey("<strong>DHL:</strong> 6 to 11 business days total. About 5 production + 1 to 6 transit.")}
                        components={{ strong: <strong /> }}
                      />
                    </li>
                  </ul>
                </li>
              </ol>
            </Stack>

            <Typography variant="body2">
              {t("These delivery times are averages and can vary depending on the product and other factors.")}
            </Typography>

            <StoreShippingTablesEmbed
              defaultView="weight"
              allowedViews={["weight"]}
              typeOptionsByCountry={{
                ca: [{ label: t("Standard"), value: "canada-expedited-parcel" }],
                us: [{ label: t("Standard"), value: "us-standard", zoneSourceValue: "usps-priority-mail" }],
                in: [
                  { label: t("International Tracked"), value: "intl-tracked" },
                  { label: t("DHL Express Worldwide"), value: "dhl-express-worldwide" },
                ],
              }}
            />
          </Stack>
        </StoreSetupCard>
      </Stack>
    </Container>
  </div>;
}
